import { ReportType, Option, UserStatus } from "../types/index";

export * from "./messageConstants";
export * from "./chatConstants";

export const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL as string;

export const ENVIRONMENT = {
  DEVELOPMENT: "development",
  PRODUCTION: "production",
}

export const PAGE_FLOW = {
  LOGIN: "login",
  SIGNUP: "signup",
  SSO: "sso",
} as const;

export const PENDING_SIGNUP_CREDENTIALS_TTL_MS = 15 * 60 * 1000;

export const PENDING_SIGNUP_CREDENTIALS_STORAGE_KEY =
  "zelpye-pending-signup-auth";

export const PENDING_ONBOARDING_USER_ID_KEY = "zelpye-pending-onboarding-user-id";

export const AUTH_FIELDS = {
  NAME: "name",
  EMAIL: "email",
  PHONE_NUMBER: "phoneNumber",
  DATE_OF_BIRTH: "dateOfBirth",
  PASSWORD: "password",
  CONFIRM_PASSWORD: "confirmPassword",
  OLD_PASSWORD: "oldPassword",
  NEW_PASSWORD: "newPassword",
  CONFIRM_NEW_PASSWORD: "confirmNewPassword",
  OTP: "otp",
  VERIFICATION_CODE: "verificationCode",
}

export const ITEM_LISTING_STATUS = [
  {
    id: "listed",
    label: "Mark as Up for Grabs",
    value: "LISTED",
    palette: "primary",
  },
  {
    id: "unlisted",
    label: "Mark as Unlisted",
    value: "UNLISTED",
    palette: "primary",
  },
  {
    id: "reserved",
    label: "Mark as Grab in Progress",
    value: "RESERVED",
    palette: "secondary",
  },
  {
    id: "sold",
    label: "Mark as Given",
    value: "SOLD",
    palette: "accent",
  },
];

/** User-facing labels for API listing status values (LISTED / UNLISTED / RESERVED / SOLD). */
export const ITEM_STATUS_DISPLAY_LABELS: Record<string, string> = {
  LISTED: "Up for Grabs",
  UNLISTED: "Unlisted",
  RESERVED: "Grab in Progress",
  SOLD: "Given",
};

export function getItemStatusDisplayLabel(status: string | undefined | null): string {
  if (!status) return "";
  const normalized = status.toUpperCase();
  return ITEM_STATUS_DISPLAY_LABELS[normalized] ?? status;
}

export const ALERT_SEVERITY = {
  INFO: "INFO",
  WARNING: "WARNING",
  CRITICAL: "CRITICAL",
};

export const API_RESPONSE_STATUS = {
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
};

export const SORT_KEYS = {
  NEAR_ME: "NEAR_ME",
  DISTANCE: "DISTANCE",
  NEWEST_FIRST: "NEWEST_FIRST",
  OLDEST_FIRST: "OLDEST_FIRST",
  ITEM_CONDITION_HIGH_TO_LOW: "ITEM_CONDITION_HIGH_TO_LOW",
  ITEM_CONDITION_LOW_TO_HIGH: "ITEM_CONDITION_LOW_TO_HIGH",
  DEFAULT: "DEFAULT",
} as const;

export type SortKey = (typeof SORT_KEYS)[keyof typeof SORT_KEYS];

export const DEFAULT_LISTING_SORT_KEY: SortKey = SORT_KEYS.NEWEST_FIRST;

export const SORT_BY_OPTIONS: Option[] = [
  {
    label: "Distance",
    value: SORT_KEYS.DISTANCE,
  },
  {
    label: "Newest First",
    value: SORT_KEYS.NEWEST_FIRST,
  },
  {
    label: "Oldest First",
    value: SORT_KEYS.OLDEST_FIRST,
  },
];

export const FETCH_TYPE = {
  NEARBY: "nearby",
  NEW: "new",
  USER_ITEMS: "user-items",
  SEARCH: "search",
  AUTO: "auto",
} as const;

export type FetchType = (typeof FETCH_TYPE)[keyof typeof FETCH_TYPE];

export const LOGIN_API_RESULT_KIND = {
  SUCCESS: "success",
  NEW_PASSWORD_REQUIRED: "new_password_required",
  SESSION: "session",
} as const;

export const AUTH_ERROR_REASON = {
  USER_NOT_CONFIRMED: "USER_NOT_CONFIRMED",
  VERIFY_EMAIL_REQUIRED: "VERIFY_EMAIL",
  BLOCKED_BY_ADMIN: "BLOCKED_BY_ADMIN",
  EMAIL_REGISTERED_VIA_SSO: "EMAIL_REGISTERED_VIA_SSO",
} as const;

export const PROFILE_ACTIONS = {
  LOGOUT: "logout",
  DEACTIVATE: "deactivate",
  DELETE: "delete",
  ACTIVATE: "activate",
  DEACTIVATED: "deactivated",
};

export const SHARE_LABELS = {
  SIGNUP: "Sign up",
  SIGNIN: "Sign in",
  LOGIN: "Log in",
  RESEND_OTP: "Resend OTP",
  SEND_VERIFICATION_CODE: "Send verification code",
  FORGOT_PASSWORD: "Forgot Password",
  CHANGE_MAIL_ID: "Change Mail ID",
  COPY_LINK: "Copy Link",
  COPIED: "Copied!",
  SHARE_LINK: "Share Link",
  SHARE_ON_SOCIAL_MEDIA: "Share on Social Media",
  SHARE_ON_WHATSAPP: "Share on WhatsApp",
  SHARE_ON_FACEBOOK: "Share on Facebook",
  SHARE_ON_TWITTER: "Share on Twitter",
  SHARE_ON_THREADS: "Share on Threads",
  SHARE_ON_INSTAGRAM: "Share on Instagram",
  SELECT_LOCATION: "Select Location",
  USE_MY_LOCATION: "Use my location",
  PICK_ON_MAP: "Pick on map",
  LOADING: "Loading...",
  SENDING: "Sending...",
  SAVING: "Saving...",
  CONTINUE: "Continue",
  SAVE_AND_CONTINUE: "Save and continue",
  BACK_TO_LOGIN: "Back to login",
  REPORT_ITEM: "Report Goodie",
  REPORT_USER: "Report User",
  FALLBACK_VALUE: "N/A",
  FALLBACK_VALUE_NUMBER: 0,
  NO_PENDING_PRODUCT_REVIEWS: "No pending goodie reviews",
  SUSPENDED_ACCOUNT_TITLE: "Account Suspended",
  SUSPENDED_ACCOUNT_DESCRIPTION: "Your account is currently suspended by the admin for violating our policies.",
  PAGE_NOT_FOUND_TITLE: "Page Not Found",
  PAGE_NOT_FOUND_DESCRIPTION:
    "The page you're looking for doesn't exist or may have been moved.",
  ACCESS_DENIED_TITLE: "Access Denied",
  ACCESS_DENIED_DESCRIPTION:
    "You don't have permission to view this page.",
  GO_TO_HOME: "Go to Home",
  NOTIFICATIONS: "Notifications",
  NOTIFICATIONS_UNREAD: "Notifications, unread",
  MESSAGES: "Messages",
  MESSAGES_UNREAD: "Messages, unread",
  FILTER_OPTIONS: {
    CATEGORY: "Category",
    ALL_CATEGORIES: "All categories",
    CONDITION: "Condition",
    ALL_CONDITIONS: "All conditions",
    RESET: "Reset",
  },
  SORT_BY: "Sort By",
  SORT_LISTING: "Sort Goodies",
  FILTER_BY: "Filter By",
  FILTER_LISTING: "Filter Goodies",
  UPLOAD_PHOTOS: "Upload photos",
  PAGE_LOADER: "Page Loader",
}

export const REPORT_STATUS = {
  OPEN: "OPEN",
  CLOSED: "CLOSED",
  PENDING: "PENDING",
}

export const ADMIN_ACTION_STATUS = {
  DEACTIVATED_BY_ADMIN: "DEACTIVATED_BY_ADMIN",
  ACTIVE: "ACTIVE",
  DEACTIVATED: "DEACTIVA",
};

export const USER_STATUS = {
  COMPLETED: "COMPLETED",
  DELETED: "DELETED",
  DEACTIVATED: "DEACTIVATED",
  ACTIVE: "ACTIVE",
  PENDING: "PENDING",
};

export const USER_STATUS_SET = new Set<UserStatus>([
  "ACTIVE",
  "DELETED",
  "DEACTIVATED",
  "PENDING",
]);

export const USER_STATUS_FLOW = {
  COMPLETED: "COMPLETED",
  ONBOARDING_REQUIRED: "ONBOARDING_REQUIRED",
  FAILED: "FAILED",
} as const;

export type UserStatusFlow = (typeof USER_STATUS_FLOW)[keyof typeof USER_STATUS_FLOW];

export const ITEM_STATUS = {
  LISTED: "LISTED",
  UNLISTED: "UNLISTED",
};

export const REPORT_TYPE = {
  ITEM: "ITEM" as ReportType,
  USER: "USER" as ReportType,
};

export const ADMIN_LABELS = {
  UNLIST: "Unlist",
  ACTIVATE: "Activate",
  DEACTIVATE: "Deactivate",
  YES_UNLIST: "Yes, Unlist",
  CANCEL: "Cancel",
  UNLIST_CONFIRMATION: "Are you sure you want to unlist this goodie?",
  ACTIONS: "Actions",
  POSTED_BY: "Posted by",
  LOGIN_TO_EXPLORE: "Login to Explore More",
  MESSAGE: "Grab It",
  SHARE: "Share",
  SHARE_ITEM: "Share Goodie",
  UNLIST_ITEM: "Unlist Goodie",
  ITEM_UNLISTED_BY_ADMIN:
    "This goodie has been unlisted by the administrator for not complying with our community guidelines and is no longer available to other users.",
  CLOSE_REPORT: "Close Report",
  OPEN_REPORT: "Open Report",
  OPEN_KEY: "open",
  CLOSE_KEY: "close",
  WARN_USER_KEY: "warn-user",
  WARN_USER: "Warn User",
  CLOSE_REPORT_KEY: "close-report",
  ACTIVATE_KEY: "activate",
  DEACTIVATE_KEY: "deactivate",
  VIEW_DETAILS: "View Details",
  NO_REPORTED_USERS: "No reported users found",
  USER: "User",
  EMAIL: "Email",
  REPORTS: "Reports",
  REPORT_STATUS_LABEL: "Report Status",
  USER_STATUS_LABEL: "User Status",
  DATE_REPORTED: "Date Reported",
  UNKNOWN_ITEM: "Unknown Goodie",
  NOT_AVAILABLE: "N/A",
};

export const NOT_FOUND_VARIANT = {
  PAGE_NOT_FOUND: "page-not-found",
  ACCESS_DENIED: "access-denied",
} as const;

export const STATUS_CLASSES = {
  DELETED: "status-deleted",
  PENDING: "status-pending",
};
export const CONDITIONS_MANAGEMENT_TYPES= {
  CATEGORIES: "categories",
  CONDITIONS: "conditions",
  REVIEW_CONDITIONS: "reviewConditions",
}

export const PAGE_STATIC_LINKS = [
  {
    label: "About Us",
    link: "/about-us",
  },
  {
    label: "Terms and Conditions",
    link: "/terms-and-conditions",
  },
  {
    label: "Privacy Policy",
    link: "/privacy-policy",
  },
]

export const WEB_FCM_TOKEN_KEY = "WEB_FCM_TOKEN";
export const WEB_PUSH_NOTIFICATIONS_ENABLED_KEY = "WEB_PUSH_NOTIFICATIONS_ENABLED";
export const DEVICE_REGISTRATION_SESSION_KEY = "DEVICE_REGISTRATION_SESSION_USER";

export const TYPE_CONSTANTS = {
  UNDEFINED: "undefined",
  PERMISSIONS: "permissions",
};

export const BUTTON_LABELS = {
  MARK_AS_READ: "Mark as read",
  DELETE_NOTIFICATION: "Delete notification",
  MARK_AS_COMPLETE: "Mark as complete",
  DEACTIVATE: "Deactivate",
  CONTACT_US: "Contact Us",
}

export const SITE_OG_DEFAULTS = {
  documentTitle: "Zelpye",
  ogTitle: "Zelpye — give & grab goodies",
  ogDescription:
    "Discover goodies on Zelpye. Give and grab pre-loved finds with confidence on Zelpye.",
  twitterTitle: "Zelpye — give & grab goodies",
  twitterDescription: "Discover goodies on Zelpye.",
} as const;

export const PROFILE_IMG_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/bmp",
] as const;

export const ADMIN_INVITE_TEXT = {
  TITLE: "Invite an Admin User",
  DESCRIPTION: "Send an invitation email with temporary credentials. The new admin must log in and set a new password.",
  SEND_INVITATION: "Send Invitation",
  SET_PASSWORD: "Set Your Password",
}

export const DEFAULT_SEARCH_DISTANCE = 50;

export const BACK_NAVIGATION_ITEMS = {
  DASHBOARD: "dashboard",
  BACK: "back",
} as const;

export const REVIEW_TYPE = {
  USERS: "users",
  ITEMS: "items",
} as const;

export const APP_DOWNLOAD_LINKS = {
  APP_STORE: "https://apps.apple.com/us/app/zelpye/",
  GOOGLE_PLAY: "https://play.google.com/store/apps/details?id=com.zelpye.android",
} as const;

/** Mirrors `GeolocationPositionError` codes from the browser API. */
export const GeolocationErrorCode = {
  PERMISSION_DENIED: 1,
  POSITION_UNAVAILABLE: 2,
  TIMEOUT: 3,
} as const;

/** localStorage key for persisted user location */
export const USER_LOCATION_STORAGE_KEY = "zelpye-user-location";

/** India geographic center — used when no saved or GPS location exists */
export const DEFAULT_LOCATION = {
  lat: 20.5937,
  lng: 78.9629,
  address: "India",
} as const;

export const IDENTITY_PROVIDER = {
  GOOGLE: "Google",
  FACEBOOK: "Facebook",
  APPLE: "SignInWithApple",
} as const;

export const FAVOURITE_BUTTON_VARIANT = {
  DEFAULT: "default",
  OVERLAY: "overlay",
} as const;
